import React, { Component } from "react";
import mapboxgl from "mapbox-gl";
import { withRouter, Link } from "react-router-dom";
import { RouteComponentProps } from "react-router";
import { connect, ConnectedProps } from "react-redux";
import { ExportOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Row, Col, Tooltip, Modal, message } from "antd";
import * as d3 from "d3-dsv";
import { saveAs } from "file-saver";
import "mapbox-gl/dist/mapbox-gl.css";
import { Nav } from "../../components";
import { INDICATORS, formatPercent } from "../../configs/constants";
import { withCommas, treeTypesByAbbr, sqft } from "../../helpers";
import { Blockgroup, Municipality } from "../../types";
import type { RootState } from "../../reducers/index";
import {
  actionScenarioGet,
  actionScenarioAdd,
  actionUserSignOut,
} from "../../actions";
import { getTesChange, getSpecificBenefit, getTotalTrees } from "./helpers";

import {
  highlightScenario,
  addVectorSources,
  addLayers,
} from "../../configs/constants";

import "./Report.css";
import municipalityReportData from "../../data/Municipalities.json";
import treeData from "../../data/speciesImpact.json";

const mapDispatchToProps = {
  actionScenarioAdd,
  actionScenarioGet,
  actionUserSignOut,
};

const mapStateToProps = (state: RootState) => {
  if (state) {
    return state;
  } else {
    return null;
  }
};

function slug(str: string) {
  str = str.replace(/^\s+|\s+$/g, "").toLowerCase();

  // remove accents, swap ñ for n, etc
  var from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;";
  var to = "aaaaeeeeiiiioooouuuunc------";
  for (var i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  }

  return str
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

const blockGroupAcresPresence = (blockgroup: Blockgroup) => {
  if (blockgroup.acresAdded && blockgroup.acresAdded > 0) {
    const treeType = treeTypesByAbbr.get(blockgroup.treeType);
    return (
      <li className="report-details-list__item">
        <div className="report-details-list__label">Your Planting Plan</div>
        <div className="report-stats">
          <div className="report-stat-row">
            <dl className="report-stat">
              <dt className="report-stat__label">Acres of new tree canopy</dt>
              <dd className="report-stat__value">
                {(blockgroup.acresAdded || 0).toFixed(2)}
                {treeType ? (
                  <div style={{ fontSize: 18, paddingTop: 10 }}>
                    {treeType.Abbr}
                    <span style={{ fontSize: 12, verticalAlign: "top" }}>
                      <Tooltip
                        placement="top"
                        title={`${treeType.Type}, ${treeType.Size}`}
                      >
                        <QuestionCircleOutlined className="icon icon--interactive" />
                      </Tooltip>
                    </span>
                  </div>
                ) : null}
              </dd>
            </dl>

            <dl className="report-stat">
              <dt className="report-stat__label">% Canopy increase</dt>
              <dd className="report-stat__value">
                {(
                  ((blockgroup.acresAdded || 0) / blockgroup.TotalAcres) *
                  100
                ).toFixed()}
                {"%"}
              </dd>
            </dl>

            <dl className="report-stat report-stat--success">
              <dt className="report-stat__label">
                Change in Tree Equity Score
              </dt>
              <dd className="report-stat__value">{getTesChange(blockgroup)}</dd>
            </dl>
          </div>
        </div>
      </li>
    );
  }
};

const processPublic = (publicString: string) => {
  if (publicString === " ") {
    return "Private";
  } else if (publicString === "Y") {
    return "Public";
  } else if (publicString === "N") {
    return "Private";
  } else {
    return publicString;
  }
};

const DrawnArea = ({ drawn_area }: { drawn_area: any }) => {
  if (drawn_area) {
    const denominator =
      (drawn_area.properties.landuse.canopy || 0) +
      (drawn_area.properties.landuse.other || 0) +
      (drawn_area.properties.landuse.water || 0) +
      (drawn_area.properties.landuse.buildings || 0) +
      (drawn_area.properties.landuse.road || 0) +
      (drawn_area.properties.landuse.grass || 0);

    const percentAs = (k: string) =>
      formatPercent((drawn_area.properties.landuse[k] || 0) / denominator);

    return (
      <React.Fragment>
        <li className="report-parcel-list__item">
          <div className="report-parcel-list__label">
            Custom Area: {drawn_area.properties.name}
          </div>
          <ul className="report-details-list">
            <li className="report-details-list__item">
              <div className="report-details-list__label">
                Your planting Plan
              </div>
              <div className="report-stats">
                <div className="report-stat-row">
                  <dl className="report-stat">
                    <dt className="report-stat__label">
                      New tree canopy (sq feet)
                    </dt>
                    <dd className="report-stat__value">
                      {withCommas(Math.round(drawn_area.properties.treesAdded))}
                    </dd>
                  </dl>

                  <dl className="report-stat">
                    <dt className="report-stat__label">% Canopy increase</dt>
                    <dd className="report-stat__value">
                      {formatPercent(
                        drawn_area.properties.treesAdded /
                          (drawn_area.properties.landuse.canopy * 10.764)
                      )}
                    </dd>
                  </dl>
                  <dl className="report-stat">
                    <dt className="report-stat__label">New Canopy %</dt>
                    <dd className="report-stat__value">
                      {formatPercent(
                        (drawn_area.properties.treesAdded +
                          drawn_area.properties.landuse.canopy * 10.764) /
                          (denominator * 10.764)
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </li>

            <li className="report-details-list__item">
              <div className="report-details-list__label">Characteristics</div>
              <div className="report-stats">
                <div className="report-stat-row">
                  <dl className="report-stat">
                    <dt className="report-stat__label">Area (sq ft)</dt>
                    <dd className="report-stat__value">
                      {withCommas(sqft(denominator))}
                    </dd>
                  </dl>

                  <dl className="report-stat">
                    <dt className="report-stat__label">
                      Plantable Area (sq ft)
                    </dt>
                    <dd className="report-stat__value">
                      {withCommas(
                        sqft(
                          (drawn_area.properties.landuse.other || 0) +
                            (drawn_area.properties.landuse.grass || 0)
                        )
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </li>

            <li className="report-details-list__item">
              <div className="report-details-list__label">Land Cover</div>
              <div className="report-stats">
                <div className="report-stat-row">
                  <dl className="report-stat">
                    <dt className="report-stat__label">Tree Canopy</dt>
                    <dd className="report-stat__value">
                      {percentAs("canopy")}
                    </dd>
                  </dl>

                  <dl className="report-stat">
                    <dt className="report-stat__label">Buildings</dt>
                    <dd className="report-stat__value">
                      {percentAs("buildings")}
                    </dd>
                  </dl>

                  <dl className="report-stat">
                    <dt className="report-stat__label">Grass/Shrub</dt>
                    <dd className="report-stat__value">{percentAs("grass")}</dd>
                  </dl>
                </div>

                <div className="report-stat-row">
                  <dl className="report-stat">
                    <dt className="report-stat__label">Roads</dt>
                    <dd className="report-stat__value">{percentAs("road")}</dd>
                  </dl>

                  <dl className="report-stat">
                    <dt className="report-stat__label">Other</dt>
                    <dd className="report-stat__value">{percentAs("other")}</dd>
                  </dl>

                  <dl className="report-stat">
                    <dt className="report-stat__label">Water</dt>
                    <dd className="report-stat__value">{percentAs("water")}</dd>
                  </dl>
                </div>
              </div>
            </li>
          </ul>
        </li>
      </React.Fragment>
    );
  } else {
    return <h1>No Drawn Areas:</h1>;
  }
};

const Parcel = ({ parcel }: { parcel: any }) => {
  return (
    <React.Fragment>
      <li className="report-parcel-list__item">
        <div className="report-parcel-list__label">Parcel: {parcel.ADDR}</div>
        <ul className="report-details-list">
          <li className="report-details-list__item">
            <div className="report-details-list__label">Your planting Plan</div>
            <div className="report-stats">
              <div className="report-stat-row">
                <dl className="report-stat">
                  <dt className="report-stat__label">
                    New tree canopy (sq feet)
                  </dt>
                  <dd className="report-stat__value">
                    {withCommas(Math.round((parcel.acresAdded || 0) * 43560))}
                  </dd>
                </dl>

                <dl className="report-stat">
                  <dt className="report-stat__label">% Canopy increase</dt>
                  <dd className="report-stat__value">
                    {formatPercent(
                      (parcel.acresAdded || 0) / parcel.TotalAcres
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </li>

          <li className="report-details-list__item">
            <div className="report-details-list__label">Characteristics</div>
            <div className="report-stats">
              <div className="report-stat-row">
                <dl className="report-stat">
                  <dt className="report-stat__label">Area (sq ft)</dt>
                  <dd className="report-stat__value">
                    {withCommas(Math.round(parcel.TotalAcres * 43560))}
                  </dd>
                </dl>

                <dl className="report-stat">
                  <dt className="report-stat__label">Plantable Area (sq ft)</dt>
                  <dd className="report-stat__value">
                    {withCommas(parcel.PossibleTC)}
                  </dd>
                </dl>
              </div>
            </div>
          </li>

          <li className="report-details-list__item">
            <div className="report-details-list__label">Land Cover</div>
            <div className="report-stats">
              <div className="report-stat-row">
                <dl className="report-stat">
                  <dt className="report-stat__label">Tree Canopy</dt>
                  <dd className="report-stat__value">
                    {formatPercent(parcel.TrCan_Pct)}
                  </dd>
                </dl>

                <dl className="report-stat">
                  <dt className="report-stat__label">Bare Earth</dt>
                  <dd className="report-stat__value">
                    {formatPercent(parcel.BaSoil_Pct)}
                  </dd>
                </dl>

                <dl className="report-stat">
                  <dt className="report-stat__label">Grass/Shrub</dt>
                  <dd className="report-stat__value">
                    {formatPercent(parcel.GrSh_Pct)}
                  </dd>
                </dl>
              </div>

              <div className="report-stat-row">
                <div className="report-stat-col">
                  <div className="report-stat-col__col">
                    <dl className="report-stat">
                      <dt className="report-stat__label">Impervious Cover</dt>
                      <dd className="report-stat__value">
                        {formatPercent(parcel.ImpePct)}
                      </dd>
                    </dl>
                  </div>

                  <div className="report-stat-col__col">
                    <dl className="report-meta-list">
                      <dt className="report-meta-list__label">Buildings:</dt>
                      <dd className="report-meta-list__value">
                        {formatPercent(parcel.Bui_Pct)}
                      </dd>

                      <dt className="report-meta-list__label">
                        Roads/Railroads:
                      </dt>
                      <dd className="report-meta-list__value">
                        {formatPercent(parcel.RoadRR_Pct)}
                      </dd>

                      <dt className="report-meta-list__label">
                        Other Impervious:
                      </dt>
                      <dd className="report-meta-list__value">
                        {formatPercent(parcel.OthPav_Pct)}
                      </dd>
                    </dl>
                  </div>
                </div>

                <div className="report-stat-col">
                  <div className="report-stat-col__col">
                    <dl className="report-meta-list report-meta-list--40-60">
                      <dt className="report-meta-list__label">Watershed:</dt>
                      <dd className="report-meta-list__value">
                        {parcel.Watershed}
                      </dd>

                      <dt className="report-meta-list__label">Flood Risk:</dt>
                      <dd className="report-meta-list__value">
                        {parcel.Flood}
                      </dd>

                      <dt className="report-meta-list__label">Ownership:</dt>
                      <dd className="report-meta-list__value">
                        {processPublic(parcel.Public)}
                      </dd>
                      <dt className="report-meta-list__label">
                        School property:
                      </dt>
                      <dd className="report-meta-list__value">
                        {parcel.School}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </li>
    </React.Fragment>
  );
};

const BlockgroupRow = ({ blockgroup }: { blockgroup: Blockgroup }) => {
  return (
    <React.Fragment>
      <li className="report-list__item">
        <div className="report-list__label">
          Block Group: {blockgroup.GEOID}
        </div>
        <ul className="report-parcel-list">
          <li className="report-parcel-list__item">
            <ul className="report-details-list">
              <li className="report-details-list__item">
                <div className="report-details-list__label">Tree Equity</div>

                <div className="report-stats">
                  <div className="report-stat-row report-stat-row--emphasized">
                    <dl className="report-stat">
                      <dt className="report-stat__label">
                        Existing Tree Canopy Cover
                      </dt>
                      <dd className="report-stat__value">
                        {formatPercent(blockgroup.TrCan_Pct)}
                      </dd>
                    </dl>

                    <dl className="report-stat">
                      <dt className="report-stat__label">
                        Tree Canopy Cover Goal
                      </dt>
                      <dd className="report-stat__value">
                        {formatPercent(blockgroup.tc_goal)}
                      </dd>
                    </dl>

                    <dl className="report-stat report-stat--success">
                      <dt className="report-stat__label">Tree Equity Score</dt>
                      <dd className="report-stat__value">
                        {blockgroup.tes.toFixed()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </li>

              <li className="report-details-list__item">
                <div className="report-details-list__label">
                  Improving your Tree Equity Score
                </div>
                <p className="report-details-list__body">
                  There are{" "}
                  <strong>
                    {withCommas(blockgroup.PossibleTC / 4046.85642)}
                  </strong>{" "}
                  acres of available plantable space in this neighborhood.
                </p>
                <p className="report-details-list__body">
                  To achieve a Tree Equity Score of 100, this neighborhood{" "}
                  <strong>
                    requires{" "}
                    {Math.max(
                      0,
                      blockgroup.tc_goal * blockgroup.TotalAcres -
                        blockgroup.TREE_CANOP / 4046.8
                    ).toFixed()}{" "}
                    additional acres
                  </strong>{" "}
                  of tree canopy.
                </p>
              </li>

              {blockGroupAcresPresence(blockgroup)}

              <li className="report-details-list__item">
                <div className="report-details-list__label">Demographics</div>
                <div className="report-stats">
                  <div className="report-stat-row">
                    <dl className="report-stat">
                      <dt className="report-stat__label">Population</dt>
                      <dd className="report-stat__value">
                        {withCommas(blockgroup.total_pop)}
                      </dd>
                    </dl>

                    <dl className="report-stat">
                      <dt className="report-stat__label">People of Color</dt>
                      <dd className="report-stat__value">
                        {formatPercent(blockgroup.pctpoc)}
                      </dd>
                    </dl>

                    <dl className="report-stat">
                      <dt className="report-stat__label">
                        Population below 200% of the poverty line
                      </dt>
                      <dd className="report-stat__value">
                        {formatPercent(blockgroup.pctpov)}
                      </dd>
                    </dl>
                  </div>

                  <div className="report-stat-row">
                    <dl className="report-stat">
                      <dt className="report-stat__label">Unemployment Rate</dt>
                      <dd className="report-stat__value">
                        {formatPercent(blockgroup.unemplrate)}
                      </dd>
                    </dl>

                    <dl className="report-stat">
                      <dt className="report-stat__label">Seniors 65+</dt>
                      <dd className="report-stat__value">
                        {formatPercent(blockgroup.seniorperc)}
                      </dd>
                    </dl>

                    <dl className="report-stat">
                      <dt className="report-stat__label">Children (0-17)</dt>
                      <dd className="report-stat__value">
                        {formatPercent(blockgroup.child_perc)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </li>

              <li className="report-details-list__item">
                <div className="report-details-list__label">
                  Climate/Environment
                </div>
                <div className="report-stats">
                  <div className="report-stat-row">
                    <dl className="report-stat">
                      <dt className="report-stat__label">
                        Average Temperature
                      </dt>
                      <dd className="report-stat__value">
                        {blockgroup.temp.toFixed()}
                      </dd>
                    </dl>

                    <dl className="report-stat">
                      <dt className="report-stat__label">
                        Carbon Storage (metric tons)
                      </dt>
                      <dd className="report-stat__value">
                        {withCommas(blockgroup.CarbonStor, 1)}
                      </dd>
                    </dl>

                    <dl className="report-stat">
                      <dt className="report-stat__label">
                        Sequestration Potential (metric tons):
                      </dt>
                      <dd className="report-stat__value">
                        {withCommas(blockgroup.SeqPotenti, 1)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </li>
            </ul>
          </li>
          <ul className="report-parcel-list">
            {blockgroup.parcels.map((parcel, index) => (
              <Parcel parcel={parcel} key={index} />
            ))}
          </ul>
          <ul className="report-parcel-list">
            {blockgroup.features &&
              blockgroup.features.map((drawn_area, index) => (
                <DrawnArea drawn_area={drawn_area} key={index} />
              ))}
          </ul>
        </ul>
      </li>
    </React.Fragment>
  );
};

const MunicipalityRow = ({ municipality }: { municipality: Municipality }) => {
  return (
    <li className="report-list__item">
      <div className="report-list__label">{municipality.NAME}</div>
      <ul className="report-list report-list--level-2">
        {municipality.blockgroups.map((blockgroup, index) => (
          <BlockgroupRow blockgroup={blockgroup} key={index} />
        ))}
      </ul>
    </li>
  );
};

type ReportMapProps = {
  scenario?: any;
};

class ReportMap extends Component<ReportMapProps> {
  map: mapboxgl.Map;
  reportMapRef: React.RefObject<HTMLDivElement>;
  constructor(props: ReportMapProps) {
    super(props);
    this.reportMapRef = React.createRef();
  }
  componentDidMount() {
    this.map = new mapboxgl.Map({
      container: this.reportMapRef.current,
      style: "mapbox://styles/tesa-app/ckf7dsrw807la1amt1fjwh7l5",
      maxPitch: 0,
      dragRotate: false,
      attributionControl: false,
      interactive: false,
      zoom: 10,
      center: [-71.4, 41.8],
    });
    this.map.once("load", () => {
      addVectorSources(this.map);
      addLayers(this.map);
      highlightScenario(this.map, this.props.scenario);
    });
  }
  render() {
    return (
      <div className="report-header__map-image" ref={this.reportMapRef}></div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
interface RouterProps {
  id: string;
}

type PropsFromRedux = ConnectedProps<typeof connector>;

type ReportProps = PropsFromRedux & RouteComponentProps<RouterProps>;

type ReportState = {
  shareModalIsVisible: boolean;
};

const Footer = () => (
  <footer className="footer">
    <div className="footer__row">
      <div className="footer__col footer__col--center">
        <ul className="footer__list">
          <li className="footer__item">
            <strong>TreeEquityScore.org</strong>
          </li>

          <li className="footer__item">Powered by American Forests</li>
        </ul>
      </div>
    </div>
  </footer>
);

class Report extends Component<ReportProps, ReportState> {
  urlInput: React.RefObject<HTMLInputElement>;
  constructor(props: ReportProps) {
    super(props);
    this.urlInput = React.createRef();
    this.state = {
      shareModalIsVisible: false,
    };
  }
  componentDidMount() {
    if ("id" in this.props.match.params) {
      this.getScenario();
    }
  }
  componentDidUpdate(prevProps: ReportProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.getScenario();
    }
  }
  getScenario() {
    this.props.actionScenarioGet({
      id: this.props.match.params.id,
    });
  }
  getIndicators(blockgroup) {
    console.log(INDICATORS);
    return {
      "Current tree canopy (acres)": (
        blockgroup.TrCan_Pct * blockgroup.TotalAcres
      ).toFixed(),
      "Current Tree Equity Score": blockgroup[
        INDICATORS.TREE_EQUITY_SCORE.field
      ].toFixed(),
      "Acres added": blockgroup.acresAdded || "N/A",
      "Total Tree Cover goal": blockgroup.PossTCPct * blockgroup.TotalAcres,
      "Tree equity score goal": blockgroup.tc_goal.toFixed(),
      "TES Change": getTesChange(blockgroup),
    };
  }

  export = () => {
    const { scenario } = this.props;
    const blockgroups = scenario.municipalities.flatMap(
      (municipality) => municipality.blockgroups
    );
    let rows = blockgroups.map((blockgroup) => {
      return {
        Municipality: blockgroup.NAME,
        Blockgroup: blockgroup.GEOID,
        ...this.getIndicators(blockgroup),
      };
    });
    saveAs(
      new Blob([d3.csvFormat(rows)], {
        type: "text/plain;charset=utf-8",
      }),
      `report-${slug(scenario.name || "")}.csv`
    );
  };

  showModal = () => {
    this.setState({
      shareModalIsVisible: true,
    });
  };

  handleCancel = () => {
    this.setState({
      shareModalIsVisible: false,
    });
  };

  handleCopyUrl = (e) => {
    e.preventDefault();
    this.urlInput.current.select();
    document.execCommand("copy");
    e.target.focus();
    message.success("Link copied to clipboard!");
  };

  shareModal = () => {
    return (
      <Modal
        title="Share this report with others"
        visible={this.state.shareModalIsVisible}
        onCancel={this.handleCancel}
        footer={null}
      >
        <p>
          <strong>Save as a pdf</strong>
        </p>
        <ol>
          <li>
            On the top left-hand corner of your screen, click File &gt; Print.
          </li>
          <li>Alternatively, press Ctrl + P (Windows) or Cmd + P (Mac)</li>
          <li>
            Under the 'Destination' section, click Change... &gt; Save as PDF.
          </li>
          <li>
            Click the 'Save' button, pick the name and location for your PDF.
          </li>
        </ol>

        <p>
          <strong>Share this report web page</strong>
        </p>
        <p>
          This report can easily be shared and accesed by others by sharing the
          url of this page.{" "}
          <a href="#copy-url" onClick={this.handleCopyUrl}>
            Copy the public share url
          </a>
        </p>
        <input
          className="accessibly-hidden"
          defaultValue={window.location.href}
          ref={this.urlInput}
          tabIndex={-1}
          readOnly
        />
      </Modal>
    );
  };

  render() {
    const { scenario } = this.props;
    if (scenario) {
      let totalBlockGroups = 0,
        totalFeatures = 0,
        totalParcels = 0;

      const { municipalities } = scenario;
      for (let municipality of municipalities) {
        if (municipality.blockgroups) {
          totalBlockGroups += municipality.blockgroups.length;
          for (let blockgroup of municipality.blockgroups) {
            if (blockgroup.parcels) {
              totalParcels += blockgroup.parcels.length;
            }
            if (blockgroup.features) {
              totalFeatures += blockgroup.features.length;
            }
          }
        }
      }

      return (
        <React.Fragment>
          <div className="report-page">
            <Nav {...this.props} />
            <div className="report-header">
              <div className="report-header__content">
                <div className="report-header__heading-container">
                  <h1 className="report-header__heading">{scenario.name}</h1>
                </div>
                <ReportMap scenario={scenario} />
              </div>
            </div>
            <div className="report-content">
              <div className="report-report-info">
                <div className="report-controls hide-for-print">
                  <Link
                    className="ant-btn"
                    to={`/scenario/${this.props.match.params.id}`}
                  >
                    &laquo; View Map
                  </Link>
                  <button onClick={this.export} className="ant-btn">
                    <ExportOutlined /> Export CSV
                  </button>
                  <button onClick={this.showModal} className="ant-btn">
                    Share
                  </button>
                </div>

                {this.shareModal()}

                <div className="report-meta-data__heading-for-print">
                  {scenario.name}
                </div>
                <h2>Project sites</h2>
                <ul className="report-sites-list">
                  <li className="report-sites-list__item">
                    {scenario.municipalities.length}{" "}
                    {scenario.municipalities.length > 1 ? "Cities" : "City"}
                  </li>
                  <li className="report-sites-list__item">
                    {totalBlockGroups}{" "}
                    {totalBlockGroups > 1 ? "Block Groups" : "Block Group"}
                  </li>
                  <li className="report-sites-list__item">
                    {totalParcels} {totalParcels > 1 ? "Parcels" : "Parcel"}
                  </li>
                  <li className="report-sites-list__item">
                    {totalFeatures}{" "}
                    {totalFeatures > 1 ? "Custom Areas" : "Custom Area"}
                  </li>
                </ul>
              </div>

              <Row gutter={40}>
                <>
                  <Col sm={15}>
                    <>
                      <ul className="report-list">
                        {scenario.municipalities &&
                          scenario.municipalities.map((municipality, index) => (
                            <MunicipalityRow
                              municipality={municipality}
                              key={index}
                            />
                          ))}
                      </ul>
                    </>
                  </Col>
                  <Col sm={9}>
                    <div className="report-impact">
                      <div className="report-impact__header">
                        <h2 className="report-impact__heading">Impact</h2>
                        <p className="report-impact__deck">
                          Estimated benefits of your tree planting project
                        </p>
                      </div>

                      <div className="report__impact-stat-grid">
                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            New Canopy (acres)
                          </dt>
                          <dd className="report__impact-value">
                            {getTotalTrees(scenario)}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Total CO2 Stored after 25 years (tons)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "CO2Total25")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            CO2 Stored (tons per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "CO2Annual")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Rain Interception (cubic meters per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "RainAnnual")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Ozone (tons per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "O3Annual")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Nitrogen Oxide (tons per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "NoxAnnual")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Particulate Matter (tons per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "PM10Annual")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Volatile Organic Compounds (tons per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "NetVOCAnnual")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Energy - Cooling (kW-h per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "CoolingAnnual")}
                          </dd>
                        </dl>

                        <dl className="report__impact-stat">
                          <dt className="report__impact-label">
                            Energy - Heating (kBTU per year)
                          </dt>
                          <dd className="report__impact-value">
                            {getSpecificBenefit(scenario, "HeatingAnnual")}
                          </dd>
                        </dl>
                      </div>

                      <div className="report-impact__footer">
                        <div className="report-impact__logo-row">
                          <div className="report-impact__logo-col">
                            <a
                              href="https://www.cityforestcredits.org/"
                              className="report-impact__logo-link"
                            >
                              <img
                                className="report-impact__logo-image report-impact__logo-image--screen"
                                src="/assets/city-forest-credits-logo.png"
                                alt="city forest credits logo"
                                width="138"
                                height="39"
                              />

                              <img
                                className="report-impact__logo-image report-impact__logo-image--print"
                                src="/assets/city-forest-credits-logo-grey.png"
                                alt="city forest credits logo"
                                width="138"
                                height="39"
                              />
                            </a>
                          </div>
                          <div className="report-impact__logo-col">
                            <p className="report-impact__body">
                              These impacts are estimated with City Forest
                              Credits Quantification Tool.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="report-impact">
                      <div className="report-impact__header">
                        <h2 className="report-impact__subhead">Resources</h2>
                        <p className="report-impact__body">
                          <a href="http://www.treeequityscore.org/">
                            Learn More About The Tree Equity Score
                          </a>
                        </p>
                        <p className="report-impact__body">
                          <a href="https://www.americanforests.org/our-work/urban-forestry/">
                            Learn More Urban Forestry And The Role Of Tree
                            Equity
                          </a>
                        </p>
                        <p className="report-impact__body">
                          <a href="https://www.vibrantcitieslab.com/resources/">
                            Vibrant Cities Lab Resource Library
                          </a>
                        </p>
                        <p className="report-impact__body">
                          <a href="https://www.vibrantcitieslab.com/resources/rhode-island-tree-equity-funding-financing-and-policy-guide/">
                            Rhode Island Tree Equity Funding, Financing and Policy Guide
                          </a>
                        </p>
                      </div>
                    </div>
                  </Col>
                </>
              </Row>
            </div>
          </div>
          <Footer />
        </React.Fragment>
      );
    } else {
      let currentCity = municipalityReportData.features.filter(
        (feat) =>
          feat.properties.NAME.toLowerCase() ===
          window.location.pathname.split("/")[1].replaceAll("_", " ")
      )[0];
      if (window.location.pathname.split("/")[2] === "report") {
        return (
          <div className="report-page">
            <Nav {...this.props} />
            <div className="report-header">
              <div className="report-header__content">
                <div className="report-header__heading-container">
                  <h1 className="report-header__heading">
                    {currentCity.properties.NAME.charAt(0).toUpperCase() +
                      currentCity.properties.NAME.slice(1).toLowerCase()}
                  </h1>
                  <h2 className="report-header__sub-heading">
                    Tree Equity Report
                  </h2>
                </div>
                <ReportMap />
              </div>
            </div>
            <div className="report-content">
              <div className="report-report-info">
                <div className="report-controls hide-for-print">
                  <Link className="ant-btn" to={`/`}>
                    &laquo; View Map
                  </Link>
                  <button onClick={this.showModal} className="ant-btn">
                    Share
                  </button>
                </div>

                {this.shareModal()}

                <div className="report-meta-data__heading-for-print">
                  {currentCity.properties.NAME.charAt(0).toUpperCase() +
                    currentCity.properties.NAME.slice(1)}
                  <br />
                  Tree Equity Score
                </div>
              </div>

              <Row gutter={40}>
                <>
                  <Col sm={15}>
                    <ul className="report-parcel-list">
                      <li className="report-parcel-list__item">
                        <ul className="report-details-list">
                          <li className="report-details-list__item">
                            <div className="report-details-list__label">
                              Tree Equity Score
                            </div>
                            <div className="report-stats">
                              <div className="report-stat-row">
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Existing Tree Canopy Cover
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.TrCan_Pct * 100
                                    ).toFixed()}
                                    {"%"}
                                  </dd>
                                </dl>
                                <dl className="report-stat report-stat--success">
                                  <dt className="report-stat__label">
                                    Tree Equity Score
                                  </dt>
                                  <dd className="report-stat__value">
                                    {currentCity.properties.TESCity}
                                  </dd>
                                </dl>
                              </div>
                            </div>
                          </li>

                          <li className="report-details-list__item">
                            <div className="report-details-list__label">
                              Neighborhood Tree Equity Scores
                            </div>
                            <div className="report-stats">
                              <div className="report-stat-row">
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Score: 0-70
                                  </dt>
                                  <dd className="report-stat__value report-stat__value--small">
                                    {currentCity.properties.BGTES70}{" "}
                                    neighborhoods
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Score: 70-79
                                  </dt>
                                  <dd className="report-stat__value report-stat__value--small">
                                    {currentCity.properties.BGTES80 -
                                      currentCity.properties.BGTES70}{" "}
                                    neighborhoods
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Score: 80-89
                                  </dt>
                                  <dd className="report-stat__value report-stat__value--small">
                                    {currentCity.properties.BGTES90 -
                                      currentCity.properties.BGTES80}{" "}
                                    neighborhoods
                                  </dd>
                                </dl>
                              </div>

                              <div className="report-stat-row">
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Score: 90-100
                                  </dt>
                                  <dd className="report-stat__value report-stat__value--small">
                                    {currentCity.properties.BGTES100 -
                                      currentCity.properties.BGTES90}{" "}
                                    neighborhoods
                                  </dd>
                                </dl>
                                <dl className="report-stat"></dl>
                              </div>
                            </div>
                          </li>

                          <li className="report-details-list__item">
                            <div className="report-details-list__label">
                              Demographics
                            </div>
                            <div className="report-stats">
                              <div className="report-stat-row">
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Population
                                  </dt>
                                  <dd className="report-stat__value">
                                    {withCommas(
                                      currentCity.properties.total_pop
                                    )}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    People of Color
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.pctpoc * 100
                                    ).toFixed()}
                                    {"%"}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Population below 200% of the poverty line
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.pctpov * 100
                                    ).toFixed()}
                                    {"%"}
                                  </dd>
                                </dl>
                              </div>

                              <div className="report-stat-row">
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Unemployment Rate
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.unemplrate * 100
                                    ).toFixed()}
                                    {"%"}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Seniors (65+)
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.seniorperc * 100
                                    ).toFixed()}
                                    {"%"}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Children (0-17)
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.child_perc * 100
                                    ).toFixed()}
                                    {"%"}
                                  </dd>
                                </dl>
                              </div>
                            </div>
                          </li>

                          <li className="report-details-list__item">
                            <div className="report-details-list__label">
                              Climate/Environment
                            </div>
                            <div className="report-stats">
                              <div className="report-stat-row">
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Average Temperature:
                                  </dt>
                                  <dd className="report-stat__value">
                                    {currentCity.properties.temp.toFixed()}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Existing Biomass:
                                  </dt>
                                  <dd className="report-stat__value">
                                    {withCommas(currentCity.properties.Biomass)}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Carbon Sequestration potential:
                                  </dt>
                                  <dd className="report-stat__value">
                                    {withCommas(
                                      currentCity.properties.TrTES100 *
                                        treeData[1].CO2Total25
                                    )}
                                  </dd>
                                </dl>
                              </div>
                            </div>
                          </li>

                          <li className="report-details-list__item">
                            <div className="report-details-list__label">
                              Land Cover
                            </div>
                            <div className="report-stats">
                              <div className="report-stat-row">
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Tree Canopy
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.TrCan_Pct * 100
                                    ).toFixed()}
                                    {"%"}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Bare Earth
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.BaSoil_Pct * 100
                                    ).toFixed(1)}
                                    {"%"}
                                  </dd>
                                </dl>
                                <dl className="report-stat">
                                  <dt className="report-stat__label">
                                    Grass/Shrub
                                  </dt>
                                  <dd className="report-stat__value">
                                    {(
                                      currentCity.properties.GrSh_Pct * 100
                                    ).toFixed(1)}
                                    {"%"}
                                  </dd>
                                </dl>
                              </div>

                              <div className="report-stat-row">
                                <div className="report-stat-col">
                                  <div className="report-stat-col__col">
                                    <dl className="report-stat">
                                      <dt className="report-stat__label">
                                        Impervious Cover
                                      </dt>
                                      <dd className="report-stat__value">
                                        {(
                                          currentCity.properties.ImpePct * 100
                                        ).toFixed()}
                                        {"%"}
                                      </dd>
                                    </dl>
                                  </div>
                                  <div className="report-stat-col__col">
                                    <dl className="report-meta-list">
                                      <dt className="report-meta-list__label">
                                        Buildings:
                                      </dt>
                                      <dd className="report-meta-list__value">
                                        {(
                                          currentCity.properties.Bui_Pct * 100
                                        ).toFixed(1)}
                                        {"%"}
                                      </dd>

                                      <dt className="report-meta-list__label">
                                        Roads/Rails:
                                      </dt>
                                      <dd className="report-meta-list__value">
                                        {(
                                          currentCity.properties.RoadRR_Pct *
                                          100
                                        ).toFixed(1)}
                                        {"%"}
                                      </dd>

                                      <dt className="report-meta-list__label">
                                        Water:
                                      </dt>
                                      <dd className="report-meta-list__value">
                                        {(
                                          currentCity.properties.Wat_Pct * 100
                                        ).toFixed(1)}
                                        {"%"}
                                      </dd>
                                    </dl>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </Col>
                  <Col sm={9}>
                    <div className="report-impact">
                      <div className="report-impact__header">
                        <h2 className="report-impact__heading">
                          Tree Planting Impacts
                        </h2>
                      </div>

                      <div className="report-impact__content">
                        <p className="report-impact__content-heading">
                          Get all {currentCity.properties.BGTES100}{" "}
                          neighborhoods to a <br />
                          <strong>tree equity score of 70</strong>
                        </p>

                        <dl className="report__impact-stat report__impact-stat--horizontal">
                          <dt className="report__impact-label">
                            Medium Sized <br />
                            Trees Needed
                          </dt>
                          <dd className="report__impact-value">
                            {withCommas(currentCity.properties.TrTES70)}
                          </dd>
                        </dl>

                        <div className="report__impact-stat-grid-label">
                          Planting Impact
                        </div>

                        <div className="report__impact-stat-grid report__impact-stat-grid--3-col report__impact-stat-grid--clean">
                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Acres of New Canopy
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                (currentCity.properties.TrTES70 *
                                  treeData[1].AreaSF) /
                                  43560
                              )}
                            </dd>
                          </dl>

                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Total CO2 Stored after 25 years (tons)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES70 *
                                  treeData[1].CO2Total25
                              )}
                            </dd>
                          </dl>

                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              CO2 Stored (tons per year)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES70 *
                                  treeData[1].CO2Annual
                              )}
                            </dd>
                          </dl>
                        </div>
                      </div>

                      <div className="report-impact__content">
                        <p className="report-impact__content-heading">
                          Get all {currentCity.properties.BGTES100}{" "}
                          neighborhoods to a <br />
                          <strong>tree equity score of 80</strong>
                        </p>

                        <dl className="report__impact-stat report__impact-stat--horizontal">
                          <dt className="report__impact-label">
                            Medium Sized <br />
                            Trees Needed
                          </dt>
                          <dd className="report__impact-value">
                            {withCommas(currentCity.properties.TrTES80)}
                          </dd>
                        </dl>

                        <div className="report__impact-stat-grid-label">
                          Planting Impact
                        </div>

                        <div className="report__impact-stat-grid report__impact-stat-grid--3-col report__impact-stat-grid--clean">
                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Acres of New Canopy
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                (currentCity.properties.TrTES80 *
                                  treeData[1].AreaSF) /
                                  43560
                              )}
                            </dd>
                          </dl>

                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Total CO2 Stored after 25 years (tons)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES80 *
                                  treeData[1].CO2Total25
                              )}
                            </dd>
                          </dl>

                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              CO2 Stored (tons per year)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES80 *
                                  treeData[1].CO2Annual
                              )}
                            </dd>
                          </dl>
                        </div>
                      </div>

                      <div className="report-impact__content">
                        <p className="report-impact__content-heading">
                          Get all {currentCity.properties.BGTES100}{" "}
                          neighborhoods to a <br />
                          <strong>tree equity score of 90</strong>
                        </p>

                        <dl className="report__impact-stat report__impact-stat--horizontal">
                          <dt className="report__impact-label">
                            Medium Sized <br />
                            Trees Needed
                          </dt>
                          <dd className="report__impact-value">
                            {withCommas(currentCity.properties.TrTES90)}
                          </dd>
                        </dl>

                        <div className="report__impact-stat-grid-label">
                          Planting Impact
                        </div>

                        <div className="report__impact-stat-grid report__impact-stat-grid--3-col report__impact-stat-grid--clean">
                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Acres of New Canopy
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                (currentCity.properties.TrTES90 *
                                  treeData[1].AreaSF) /
                                  43560
                              )}
                            </dd>
                          </dl>

                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Total CO2 Stored after 25 years (tons)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES90 *
                                  treeData[1].CO2Total25
                              )}
                            </dd>
                          </dl>

                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              CO2 Stored (tons per year)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES90 *
                                  treeData[1].CO2Annual
                              )}
                            </dd>
                          </dl>
                        </div>
                      </div>

                      <div className="report-impact__content">
                        <p className="report-impact__content-heading">
                          Get all {currentCity.properties.BGTES100}{" "}
                          neighborhoods to a <br />
                          <strong>tree equity score of 100</strong>
                        </p>

                        <dl className="report__impact-stat report__impact-stat--horizontal">
                          <dt className="report__impact-label">
                            Medium Sized <br />
                            Trees Needed
                          </dt>
                          <dd className="report__impact-value">
                            {withCommas(currentCity.properties.TrTES100)}
                          </dd>
                        </dl>

                        <div className="report__impact-stat-grid-label">
                          Planting Impact
                        </div>

                        <div className="report__impact-stat-grid report__impact-stat-grid--3-col report__impact-stat-grid--clean">
                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Acres of New Canopy
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                (currentCity.properties.TrTES100 *
                                  treeData[1].AreaSF) /
                                  43560
                              )}
                            </dd>
                          </dl>

                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              Total CO2 Stored after 25 years (tons)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES100 *
                                  treeData[1].CO2Total25
                              )}
                            </dd>
                          </dl>
                          <dl className="report__impact-stat">
                            <dt className="report__impact-label">
                              CO2 Stored (tons per year)
                            </dt>
                            <dd className="report__impact-value">
                              {withCommas(
                                currentCity.properties.TrTES100 *
                                  treeData[1].CO2Annual
                              )}
                            </dd>
                          </dl>
                        </div>
                      </div>

                      <div className="report-impact__footer">
                        <div className="report-impact__logo-row">
                          <div className="report-impact__logo-col">
                            <a
                              href="https://www.cityforestcredits.org/"
                              className="report-impact__logo-link"
                            >
                              <img
                                className="report-impact__logo-image report-impact__logo-image--screen"
                                src="/assets/city-forest-credits-logo.png"
                                alt="city forest credits logo"
                                width="138"
                                height="39"
                              />

                              <img
                                className="report-impact__logo-image report-impact__logo-image--print"
                                src="/assets/city-forest-credits-logo-grey.png"
                                alt="city forest credits logo"
                                width="138"
                                height="39"
                              />
                            </a>
                          </div>
                          <div className="report-impact__logo-col">
                            <p className="report-impact__body">
                              These impacts are estimated with City Forest
                              Credits Quantification Tool.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </>
              </Row>
            </div>
            <Footer />
          </div>
        );
      }
    }
    return null;
  }
}

const ReportContainer = connector(Report);

export default withRouter(ReportContainer);
